import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  Eye
} from 'lucide-react';

const SEVERITY_COLORS = {
  CRITICA: '#ef4444',
  ALTA: '#f97316',
  MEDIA: '#eab308',
  BAJA: '#22c55e'
};

const DAYS = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
const MONTHS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 
                'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

export const AlertsCalendar = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [alerts, setAlerts] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [filterSeverity, setFilterSeverity] = useState('all');
  
  useEffect(() => {
    loadAlerts();
  }, [currentDate]);

  const loadAlerts = async () => {
    setLoading(true);
    try {
      const response = await api.get('/alerts?limit=1000');
      setAlerts(response.data);
    } catch (error) {
      console.error('Error cargando alertas:', error);
      toast.error('Error al cargar alertas');
    } finally {
      setLoading(false);
    }
  };

  // Obtener días del mes
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    
    return { daysInMonth, startingDay };
  };

  // Agrupar alertas por día
  const getAlertsForDay = (day) => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    return alerts.filter(alert => {
      const alertDate = new Date(alert.created_at);
      return alertDate.getFullYear() === year && 
             alertDate.getMonth() === month && 
             alertDate.getDate() === day &&
             (filterSeverity === 'all' || alert.severidad === filterSeverity);
    });
  };

  // Navegación de mes
  const prevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
    setSelectedDate(null);
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
    setSelectedDate(null);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
    setSelectedDate(new Date().getDate());
  };

  const { daysInMonth, startingDay } = getDaysInMonth(currentDate);

  // Alertas del día seleccionado
  const selectedDayAlerts = selectedDate ? getAlertsForDay(selectedDate) : [];

  // Estadísticas del mes
  const monthAlerts = alerts.filter(alert => {
    const alertDate = new Date(alert.created_at);
    return alertDate.getFullYear() === currentDate.getFullYear() && 
           alertDate.getMonth() === currentDate.getMonth();
  });

  const monthStats = {
    total: monthAlerts.length,
    criticas: monthAlerts.filter(a => a.severidad === 'CRITICA').length,
    altas: monthAlerts.filter(a => a.severidad === 'ALTA').length,
    medias: monthAlerts.filter(a => a.severidad === 'MEDIA').length,
    bajas: monthAlerts.filter(a => a.severidad === 'BAJA').length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Cargando calendario...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 h-full overflow-auto bg-slate-50" data-testid="alerts-calendar-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
        <div>
          <h1 className="text-3xl font-heading font-bold tracking-tight text-slate-900">
            Calendario de Alertas
          </h1>
          <p className="text-slate-600 mt-1">
            Vista temporal de alertas generadas
          </p>
        </div>
        <div className="flex gap-2 mt-4 lg:mt-0">
          <Select value={filterSeverity} onValueChange={setFilterSeverity}>
            <SelectTrigger className="w-40" style={{ backgroundColor: '#ffffff' }}>
              <SelectValue placeholder="Severidad" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              <SelectItem value="CRITICA">Crítica</SelectItem>
              <SelectItem value="ALTA">Alta</SelectItem>
              <SelectItem value="MEDIA">Media</SelectItem>
              <SelectItem value="BAJA">Baja</SelectItem>
            </SelectContent>
          </Select>
          <Button
            onClick={goToToday}
            variant="outline"
            style={{ borderColor: '#cbd5e1', color: '#374151' }}
          >
            Hoy
          </Button>
        </div>
      </div>

      {/* Stats del mes */}
      <div className="grid grid-cols-5 gap-3 mb-6">
        <Card className="p-3 bg-white border-slate-200 text-center">
          <p className="text-2xl font-bold text-slate-900">{monthStats.total}</p>
          <p className="text-xs text-slate-500">Total</p>
        </Card>
        <Card className="p-3 bg-white border-slate-200 text-center">
          <p className="text-2xl font-bold" style={{ color: SEVERITY_COLORS.CRITICA }}>{monthStats.criticas}</p>
          <p className="text-xs text-slate-500">Críticas</p>
        </Card>
        <Card className="p-3 bg-white border-slate-200 text-center">
          <p className="text-2xl font-bold" style={{ color: SEVERITY_COLORS.ALTA }}>{monthStats.altas}</p>
          <p className="text-xs text-slate-500">Altas</p>
        </Card>
        <Card className="p-3 bg-white border-slate-200 text-center">
          <p className="text-2xl font-bold" style={{ color: SEVERITY_COLORS.MEDIA }}>{monthStats.medias}</p>
          <p className="text-xs text-slate-500">Medias</p>
        </Card>
        <Card className="p-3 bg-white border-slate-200 text-center">
          <p className="text-2xl font-bold" style={{ color: SEVERITY_COLORS.BAJA }}>{monthStats.bajas}</p>
          <p className="text-xs text-slate-500">Bajas</p>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendario */}
        <Card className="lg:col-span-2 p-6 bg-white border-slate-200">
          {/* Header del calendario */}
          <div className="flex items-center justify-between mb-6">
            <Button variant="ghost" onClick={prevMonth} size="sm">
              <ChevronLeft size={20} />
            </Button>
            <h2 className="text-xl font-bold text-slate-900">
              {MONTHS[currentDate.getMonth()]} {currentDate.getFullYear()}
            </h2>
            <Button variant="ghost" onClick={nextMonth} size="sm">
              <ChevronRight size={20} />
            </Button>
          </div>

          {/* Días de la semana */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {DAYS.map(day => (
              <div key={day} className="text-center text-sm font-medium text-slate-500 py-2">
                {day}
              </div>
            ))}
          </div>

          {/* Días del mes */}
          <div className="grid grid-cols-7 gap-1">
            {/* Espacios vacíos antes del primer día */}
            {Array.from({ length: startingDay }).map((_, i) => (
              <div key={`empty-${i}`} className="aspect-square"></div>
            ))}
            
            {/* Días del mes */}
            {Array.from({ length: daysInMonth }).map((_, i) => {
              const day = i + 1;
              const dayAlerts = getAlertsForDay(day);
              const isToday = new Date().toDateString() === new Date(currentDate.getFullYear(), currentDate.getMonth(), day).toDateString();
              const isSelected = selectedDate === day;
              
              // Obtener la severidad más alta del día
              const maxSeverity = dayAlerts.reduce((max, alert) => {
                const order = ['BAJA', 'MEDIA', 'ALTA', 'CRITICA'];
                return order.indexOf(alert.severidad) > order.indexOf(max) ? alert.severidad : max;
              }, 'BAJA');

              return (
                <button
                  key={day}
                  onClick={() => setSelectedDate(day)}
                  className={`
                    aspect-square rounded-lg p-1 transition-all relative
                    ${isSelected ? 'ring-2 ring-blue-500 bg-blue-50' : 'hover:bg-slate-100'}
                    ${isToday ? 'font-bold' : ''}
                  `}
                  style={isToday && !isSelected ? { backgroundColor: '#eff6ff' } : {}}
                >
                  <span className={`text-sm ${isToday ? 'text-blue-600' : 'text-slate-700'}`}>
                    {day}
                  </span>
                  {dayAlerts.length > 0 && (
                    <div className="absolute bottom-1 left-1/2 transform -translate-x-1/2 flex gap-0.5">
                      <span 
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: SEVERITY_COLORS[maxSeverity] }}
                      ></span>
                      {dayAlerts.length > 1 && (
                        <span className="text-[10px] text-slate-500">+{dayAlerts.length - 1}</span>
                      )}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </Card>

        {/* Panel lateral - Alertas del día seleccionado */}
        <Card className="p-6 bg-white border-slate-200">
          <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
            <CalendarIcon size={20} className="text-blue-600" />
            {selectedDate 
              ? `${selectedDate} de ${MONTHS[currentDate.getMonth()]}`
              : 'Selecciona un día'
            }
          </h3>
          
          {selectedDate ? (
            selectedDayAlerts.length > 0 ? (
              <div className="space-y-3 max-h-[500px] overflow-y-auto">
                {selectedDayAlerts.map(alert => (
                  <div 
                    key={alert.id}
                    className="p-3 rounded-lg border border-slate-200 hover:border-blue-300 transition-colors cursor-pointer"
                    onClick={() => navigate(`/m/monitoreo-transaccional/alerts/${alert.id}`)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="font-medium text-slate-900 text-sm line-clamp-1">
                          {alert.titulo}
                        </p>
                        <p className="text-xs text-slate-500 mt-1">
                          {new Date(alert.created_at).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                      <span 
                        className="px-2 py-0.5 rounded text-xs font-medium text-white"
                        style={{ backgroundColor: SEVERITY_COLORS[alert.severidad] }}
                      >
                        {alert.severidad}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-600">
                        {alert.estado}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-400">
                <AlertTriangle size={32} className="mx-auto mb-2 opacity-50" />
                <p>Sin alertas este día</p>
              </div>
            )
          ) : (
            <div className="text-center py-8 text-slate-400">
              <CalendarIcon size={32} className="mx-auto mb-2 opacity-50" />
              <p>Haz clic en un día para ver sus alertas</p>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default AlertsCalendar;
